/**
 * Couleurs de la marque posées sur les variables CSS « canaux » du thème
 * (`index.css`, consommées par `tailwind.config.js`), sans rechargement ni
 * rebuild.
 *
 * Une couleur égale à celle livrée par défaut rend la main aux valeurs
 * d'`index.css`, dont les teintes claires et foncées sont réglées à la main.
 * Toute autre couleur reçoit des teintes calculées par mélange.
 */

export interface CouleursMarque {
  couleurPrimaire?: string | null;
  couleurAccent?: string | null;
  couleurSurlignage?: string | null;
  couleurFond?: string | null;
}

type Rvb = [number, number, number];

const BLANC: Rvb = [255, 255, 255];
const NOIR: Rvb = [0, 0, 0];

/** Chaque couleur de marque, sa valeur livrée, sa variable et ses teintes dérivées. */
const VARIABLES: {
  cle: keyof CouleursMarque;
  defaut: string;
  variable: string;
  teintes: { variable: string; vers: Rvb; part: number }[];
}[] = [
  {
    cle: 'couleurPrimaire',
    defaut: '#006B3F',
    variable: '--c-primary',
    teintes: [
      { variable: '--c-primary-light', vers: BLANC, part: 0.15 },
      { variable: '--c-primary-dark', vers: NOIR, part: 0.27 },
    ],
  },
  {
    cle: 'couleurAccent',
    defaut: '#C4704B',
    variable: '--c-accent',
    teintes: [{ variable: '--c-accent-light', vers: BLANC, part: 0.3 }],
  },
  { cle: 'couleurSurlignage', defaut: '#E8C97A', variable: '--c-highlight', teintes: [] },
  { cle: 'couleurFond', defaut: '#FAF6F0', variable: '--c-canvas', teintes: [] },
];

/** Clé du stockage local où survivent, d'un chargement à l'autre, les couleurs enregistrées. */
const CLE_MEMOIRE = 'mirsad.couleurs-marque';

function versRvb(hex: string): Rvb | null {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

const canaux = ([r, g, b]: Rvb) => `${r} ${g} ${b}`;

const melanger = (couleur: Rvb, vers: Rvb, part: number): Rvb => [
  Math.round(couleur[0] + (vers[0] - couleur[0]) * part),
  Math.round(couleur[1] + (vers[1] - couleur[1]) * part),
  Math.round(couleur[2] + (vers[2] - couleur[2]) * part),
];

/**
 * Pose les couleurs données sur la racine du document. Une couleur absente ou
 * illisible laisse sa variable telle quelle.
 */
export function appliquerCouleursMarque(couleurs: CouleursMarque | null | undefined): void {
  if (!couleurs) return;
  const style = document.documentElement.style;
  for (const { cle, defaut, variable, teintes } of VARIABLES) {
    const hex = couleurs[cle];
    const rvb = typeof hex === 'string' ? versRvb(hex) : null;
    if (typeof hex !== 'string' || !rvb) continue;

    if (hex.replace('#', '').toUpperCase() === defaut.slice(1)) {
      style.removeProperty(variable);
      for (const teinte of teintes) style.removeProperty(teinte.variable);
      continue;
    }
    style.setProperty(variable, canaux(rvb));
    for (const teinte of teintes) {
      style.setProperty(teinte.variable, canaux(melanger(rvb, teinte.vers, teinte.part)));
    }
  }
}

/**
 * Retient les couleurs enregistrées de la marque, pour les poser dès le
 * prochain chargement, avant même la réponse de l'API. Réservé aux couleurs
 * enregistrées : un aperçu abandonné ne doit pas survivre au rechargement.
 */
export function memoriserCouleursMarque(couleurs: CouleursMarque | null | undefined): void {
  if (!couleurs) return;
  const { couleurPrimaire, couleurAccent, couleurSurlignage, couleurFond } = couleurs;
  try {
    localStorage.setItem(
      CLE_MEMOIRE,
      JSON.stringify({ couleurPrimaire, couleurAccent, couleurSurlignage, couleurFond }),
    );
  } catch {
    // Stockage indisponible (navigation privée, quota) : la marque arrivera par l'API.
  }
}

/** Pose, avant le premier rendu, les couleurs retenues au chargement précédent. */
export function appliquerCouleursMemorisees(): void {
  try {
    const brut = localStorage.getItem(CLE_MEMOIRE);
    const couleurs: unknown = brut ? JSON.parse(brut) : null;
    if (typeof couleurs === 'object' && couleurs !== null) {
      appliquerCouleursMarque(couleurs as CouleursMarque);
    }
  } catch {
    // Mémoire illisible : les couleurs arriveront avec la marque.
  }
}
